import { Grid, Typography } from "@material-ui/core";
import PreviewDocument from "components/PreviewDocument";
import FormDropdown from "components/FormFields/FormDropdown";
import FormDropzoneField from "components/FormFields/FormDropzoneField";
import FormInputV2 from "components/FormFields/FormInputV2";
import FormMaskedInput from "components/FormFields/FormMaskedInput";
import FormDatePicker from "components/Forms/FormDatePicker";
import { AO_IBAN_PREFIX, INPUT_MASKS } from "utils/const";
import {
  getBirthDateRules,
  getIbanRules,
  getIdentityDocumentExpiryRules,
  REQUIRED_FIELD_RULE,
} from "../cooperativeMember.utils";

const sectionTitleStyle = { color: "#002e9e" };
const topSectionTitleStyle = { ...sectionTitleStyle, marginTop: "1rem" };
const documentLabelStyle = { marginBottom: "0.5rem" };
const documentValueStyle = { fontWeight: "bold" };

const getPreviewableDocuments = (existingDocuments = []) =>
  existingDocuments.filter((entry) => entry?.document?.documentKey && entry?.document?.documentName);

const getReadonlyDocuments = (existingDocuments = []) =>
  existingDocuments.filter((entry) => !entry?.document?.documentKey && entry?.document?.documentName);

const CooperativeMemberFormFields = ({
  methods,
  professionalCategoryOptions,
  documentTypeOptions,
  disabledFields = {},
  showIdentitySectionTitle = false,
  existingDocuments = [],
  previewDocumentPromise,
  showUploadSection = false,
  uploadFieldsRequired = false,
}) => {
  const { control, getValues, setValue, trigger } = methods;
  const previewableDocuments = getPreviewableDocuments(existingDocuments);
  const readonlyDocuments = getReadonlyDocuments(existingDocuments);

  return (
    <>
      <Grid item xs={12} md={12} style={topSectionTitleStyle}>
        <Typography variant="h2" style={sectionTitleStyle}>
          Dados Pessoais
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <FormInputV2
          name="name"
          label="Nome"
          placeholder="Insira o nome do membro"
          required
          control={control}
          rules={REQUIRED_FIELD_RULE}
          disabled={disabledFields.name}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FormDropdown
          label="Função"
          placeholder="Seleccione a função"
          control={control}
          name="functionId"
          options={professionalCategoryOptions}
          required
          disabled={disabledFields.functionId}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <FormDatePicker
          name="dateOfBirth"
          label="Data de Nascimento"
          control={control}
          getValues={getValues}
          setValue={setValue}
          trigger={trigger}
          maxDateToday={true}
          minDateToday={false}
          isRequired={true}
          rules={getBirthDateRules()}
          disabled={disabledFields.dateOfBirth}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <FormInputV2
          name="nif"
          label="NIF"
          placeholder="Insira o NIF"
          required
          control={control}
          rules={REQUIRED_FIELD_RULE}
          disabled={disabledFields.nif}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <FormMaskedInput
          name="bankAccountNumber"
          label="Nº Conta Bancária"
          placeholder="AO06 seguido de 21 dígitos"
          control={control}
          mask={INPUT_MASKS.IBAN_AO}
          emptyMaskedValue={AO_IBAN_PREFIX}
          required
          rules={getIbanRules()}
          disabled={disabledFields.bankAccountNumber}
        />
      </Grid>

      {showIdentitySectionTitle && (
        <Grid item xs={12} md={12} style={topSectionTitleStyle}>
          <Typography variant="h2" style={sectionTitleStyle}>
            Documento de Identificação
          </Typography>
        </Grid>
      )}

      <Grid item xs={12} md={showIdentitySectionTitle ? 4 : 3}>
        <FormDropdown
          label="Documento de Identificação"
          placeholder="Seleccione o tipo de documento"
          control={control}
          name="documentTypeId"
          options={documentTypeOptions}
          required
          disabled={disabledFields.documentTypeId}
        />
      </Grid>

      <Grid item xs={12} md={showIdentitySectionTitle ? 4 : 3}>
        <FormInputV2
          name="identityDocumentNumber"
          label="Nº Documento de Identificação"
          placeholder="Insira o nº do documento"
          required
          control={control}
          rules={REQUIRED_FIELD_RULE}
          disabled={disabledFields.identityDocumentNumber}
        />
      </Grid>

      <Grid item xs={12} md={showIdentitySectionTitle ? 4 : 3}>
        <FormDatePicker
          name="identityDocumentIssueDate"
          label="Data de Emissão"
          control={control}
          getValues={getValues}
          setValue={setValue}
          trigger={trigger}
          maxDateToday={true}
          minDateToday={false}
          isRequired={true}
          disabled={disabledFields.identityDocumentIssueDate}
        />
      </Grid>

      <Grid item xs={12} md={showIdentitySectionTitle ? 4 : 3}>
        <FormDatePicker
          name="identityDocumentExpiryDate"
          label="Data de Validade"
          control={control}
          getValues={getValues}
          setValue={setValue}
          trigger={trigger}
          maxDateToday={false}
          minDateToday={true}
          isRequired={true}
          rules={getIdentityDocumentExpiryRules()}
          disabled={disabledFields.identityDocumentExpiryDate}
        />
      </Grid>

      {previewableDocuments.map(({ key, label, document }) => (
        <Grid item xs={12} md={6} key={key}>
          <PreviewDocument
            title={label}
            documents={[
              {
                documentId: document.documentKey,
                documentName: document.documentName,
              },
            ]}
            promise={previewDocumentPromise}
          />
        </Grid>
      ))}

      {readonlyDocuments.map(({ key, label, document }) => (
        <Grid item xs={12} md={6} key={`${key}-readonly`}>
          <Typography variant="body2" style={documentLabelStyle}>
            {label}
          </Typography>
          <Typography variant="body1" style={documentValueStyle}>
            {document.documentName}
          </Typography>
        </Grid>
      ))}

      {showUploadSection && (
        <>
          <Grid item xs={12} md={12} style={topSectionTitleStyle}>
            <Typography variant="h2" style={sectionTitleStyle}>
              Documentos
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormDropzoneField
              accept="application/pdf"
              control={control}
              label="Documento de Identificação"
              name="identityDocument"
              inputLabel="Adicionar documento"
              maxSize={2}
              required={uploadFieldsRequired}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormDropzoneField
              accept="application/pdf"
              control={control}
              label="Certificado de Empreendedorismo"
              name="entrepreneurshipCertificate"
              inputLabel="Adicionar documento"
              maxSize={2}
              required={uploadFieldsRequired}
            />
          </Grid>
        </>
      )}
    </>
  );
};

export default CooperativeMemberFormFields;